import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ORG_REF, PHYSICIAN_ID, PHYSICIAN_SAME_AS } from "@/lib/medical-schema";
import { EditableText } from "@/components/site/EditableText";
import { PageSection } from "@/components/site/PageSection";
import gabby from "@/assets/team-gabby-marinelli.jpg.asset.json";
import corinne from "@/assets/team-corinne-singh.jpg.asset.json";
import val from "@/assets/team-val-placencia.jpg.asset.json";
import susanna from "@/assets/team-susanna-carter.jpg.asset.json";
import jamie from "@/assets/team-jamie-cielinski.jpg.asset.json";
import keidi from "@/assets/keidi-rivera.jpg.asset.json";
import suzanne from "@/assets/suzanne-howell.jpg.asset.json";
import giselle from "@/assets/giselle-brito-lopez.jpg.asset.json";
import giselle180 from "@/assets/giselle-brito-lopez-180.webp.asset.json";
import giselle360 from "@/assets/giselle-brito-lopez-360.webp.asset.json";
import keidi180 from "@/assets/keidi-rivera-180.webp.asset.json";
import keidi360 from "@/assets/keidi-rivera-360.webp.asset.json";

export const Route = createFileRoute("/team")({
  head: () => ({
    meta: [
      { title: "Meet Our Team | Virginia Bariatric" },
      {
        name: "description",
        content:
          "Meet the caring bariatric surgery staff at Virginia Bariatric Surgery, led by Dr. Matthew Fitzer.",
      },
      ...(import.meta.env.SSR ? [
        { property: "og:title", content: "Meet Our Team | Virginia Bariatric" },
        { property: "og:description", content: "Meet the caring bariatric surgery staff at Virginia Bariatric Surgery, led by Dr. Matthew Fitzer." },
        { property: "og:url", content: "https://virginiabariatric.com/team" },
        { property: "og:type", content: "website" },
        { property: "og:image", content: "https://virginiabariatric.com/__l5e/assets-v1/64a2217d-5454-444b-b82e-298e33a87aa9/og-hero.jpg" },
        { property: "og:image:alt", content: "Virginia Bariatric Surgery — Dr. Matthew Fitzer's Northern Virginia weight-loss practice" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:image", content: "https://virginiabariatric.com/__l5e/assets-v1/64a2217d-5454-444b-b82e-298e33a87aa9/og-hero.jpg" },
        { name: "twitter:image:alt", content: "Virginia Bariatric Surgery — Dr. Matthew Fitzer's Northern Virginia weight-loss practice" },
        { name: "twitter:title", content: "Meet Our Team | Virginia Bariatric" },
        { name: "twitter:description", content: "Meet the caring bariatric surgery staff at Virginia Bariatric Surgery, led by Dr. Matthew Fitzer." },
      ] : []),
    ],
    links: [
      { rel: "canonical", href: "https://virginiabariatric.com/team" },
      {
        rel: "preload",
        as: "image",
        href: "/img/matthew-fitzer-team-360.webp",
        type: "image/webp",
        fetchPriority: "high",
      },
    ],
    scripts: import.meta.env.SSR ? [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ProfilePage",
          url: "https://virginiabariatric.com/team",
          name: "Meet Our Team | Virginia Bariatric",
          about: ORG_REF,
          mainEntity: {
            "@type": "MedicalOrganization",
            "@id": "https://virginiabariatric.com/#organization",
            name: "Virginia Bariatric Surgery",
            url: "https://virginiabariatric.com",
            telephone: "+1-703-709-9771",
            address: {
              "@type": "PostalAddress",
              streetAddress: "171 Elden Street, Suite 3B",
              addressLocality: "Herndon",
              addressRegion: "VA",
              postalCode: "20170",
              addressCountry: "US",
            },
            medicalSpecialty: "https://schema.org/Surgical",
            employee: TEAM_MEMBERS.map((member) => {
              const isSurgeon = member.name === "Matthew Fitzer, MD";
              return {
                "@type": "Person",
                ...(isSurgeon
                  ? {
                      "@id": PHYSICIAN_ID,
                      honorificSuffix: "MD",
                      url: "https://virginiabariatric.com/about",
                      mainEntityOfPage: "https://virginiabariatric.com/about",
                      sameAs: PHYSICIAN_SAME_AS,
                      knowsAbout: ["Bariatric surgery", "Obesity medicine", "Weight-loss surgery"],
                    }
                  : { url: "https://virginiabariatric.com/team" }),
                name: member.name,
                jobTitle: member.role,
                worksFor: ORG_REF,
                ...(member.bio ? { description: member.bio } : {}),
                ...(member.image
                  ? {
                      image: {
                        "@type": "ImageObject",
                        contentUrl: `https://virginiabariatric.com${member.image}`,
                        url: `https://virginiabariatric.com${member.image}`,
                        caption: member.imageAlt,
                      },
                    }
                  : {}),
              };
            }),
          },
        }),
      },
    ] : [],
  }),

  component: TeamPage,
});

type TeamMember = { name: string; role: string; image?: string; thumbnail?: string; thumbnailSrcSet?: string; imageAlt: string; bio?: string; bioLink?: string };

const TEAM_MEMBERS: TeamMember[] = [
  {
    name: "Matthew Fitzer, MD",
    role: "Bariatric Surgeon",
    image: "/img/matthew-fitzer-team-360.webp",
    imageAlt: "Matthew Fitzer, MD at Virginia Bariatric Surgery",
    bio: "Dr. Matthew Fitzer is a fellowship-trained bariatric surgeon who has focused on providing excellent care to overweight and obese patients since 2005. Since 2012, he has been serving patients in Herndon, Reston, Fairfax, Arlington, Alexandria, Loudoun County, and the DC metro area.\n\nHe is certified by the American Board of Surgery and is a fellow of the American College of Surgeons and the American Society of Metabolic and Bariatric Surgeons. He has founded and served as the Medical Director of MBSAQIP-Certified bariatric programs at multiple institutions. He has authored book chapters and articles that have been published in prestigious journals including the New England Journal of Medicine.\n\nDr. Fitzer has performed more than 6,000 laparoscopic weight-loss operations and managed weight-loss medication treatment for more than 10,000 patients in the 21+ years that he has been helping people reach their weight-loss goals.",
    bioLink: "/about",
  },
  {
    name: "Corinne Singh, PA-C",
    role: "Physician Assistant",
    image: corinne.url,
    imageAlt: "Corinne Singh, physician assistant at Virginia Bariatric Surgery",
    bio: "Corinne earned her bachelor’s degree in Anthropology from UVA in 2019. After graduating, she worked at UVA Hospital as a laboratory technician. Her lab experience led her to pursue a career in medicine.\n\nShe went on to attend James Madison University’s Physician Assistant program. She graduated in 2023 and has been with Virginia Bariatric ever since. Corinne is a patient favorite at Virginia Bariatric. She's attentive and compassionate, and she tends to build very strong relationships with her patients.\n\nWhen Corinne got married in 2025, the entire office staff came to Charlottesville for the wedding and the celebration. She enjoys walking her dog Lucy, cross-stitching, and reality TV.",
  },
  {
    name: "Suzanne Howell, RD",
    role: "Registered Dietitian",
    image: suzanne.url,
    imageAlt: "Suzanne Howell, registered dietitian at Virginia Bariatric Surgery",
    bio: "Suzanne is a Registered Dietitian and Certified Personal Trainer with 26 years of experience helping people achieve their weight loss and wellness goals. She attended the University of North Florida for her undergraduate studies, then Sage Graduate School in upstate New York. She truly loves what she does and is passionate about making healthy living practical, sustainable, and enjoyable. Outside of work, she's a mom of two and an avid runner and hiker, so she understands firsthand the importance of balancing nutrition, fitness, family, and everyday life.",
  },
  {
    name: "Val Placencia",
    role: "Team Leader",
    image: val.url,
    imageAlt: "Val Placencia, team leader at Virginia Bariatric Surgery",
    bio: "Val is the Office Team Leader at Virginia Bariatric, where she oversees daily operations, supports the team, and helps ensure the delivery of high-quality, patient-centered service. She brings more than 15 years of experience in customer service and administrative support.\n\nVal joined Virginia Bariatric in 2022 as a Patient Coordinator and was promoted to surgical scheduler for Dr. Fitzer before stepping into her current role as Team Leader. She is committed to helping all of our patients succeed in reaching their weight loss goal by whatever path they choose. Outside the office, Val is a devoted wife and mother who treasures time with her family.",
  },
  {
    name: "Gabby Marinelli, PA-C",
    role: "Physician Assistant",
    image: gabby.url,
    imageAlt: "Gabby Marinelli, physician assistant at Virginia Bariatric Surgery",
  },
  {
    name: "Giselle Brito-Lopez",
    role: "Patient Liaison",
    image: giselle.url,
    thumbnail: giselle180.url,
    thumbnailSrcSet: `${giselle180.url} 180w, ${giselle360.url} 360w`,
    imageAlt: "Giselle Brito-Lopez, patient liaison at Virginia Bariatric Surgery",
    bio: "I got into healthcare sort of by accident, but I love it. I’ve been at it for five years now, and every day I enjoy making a positive difference in the lives of the patients I serve. My hope is that each patient comes away from their visit with us feeling valued, respected, and cared for.\n\nOutside of work, I am a devoted wife and mother of two. I treasure time spent with my family, as they are my greatest source of love, inspiration, and motivation. They motivate me to keep growing and working hard, and to become the best version of myself, personally and professionally.\n\nPassion for healthcare and commitment to my family are the two guiding forces in my life. Whether I am supporting patients in my professional role or creating meaningful memories with my loved ones, I strive to lead with compassion, dedication, and a genuine heart for others.",
  },
  {
    name: "Jamie Cielinski",
    role: "Billing Manager",
    image: jamie.url,
    imageAlt: "Jamie Cielinski, billing manager at Virginia Bariatric Surgery",
  },
  {
    name: "Keidi Rivera",
    role: "Medical Assistant",
    image: keidi.url,
    thumbnail: keidi180.url,
    thumbnailSrcSet: `${keidi180.url} 180w, ${keidi360.url} 360w`,
    imageAlt: "Keidi Rivera, medical assistant at Virginia Bariatric Surgery",
  },
  {
    name: "Susanna Carter, PA-C",
    role: "Physician Assistant",
    image: susanna.url,
    imageAlt: "Susanna Carter, PA-C at Virginia Bariatric Surgery",
  }];

function initialsOf(name: string) {
  return name
    .replace(/,.*$/, "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");
}


function BioDialog({
  member,
  paragraphs,
  onClose,
}: {
  member: TeamMember;
  paragraphs: string[];
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Bio for ${member.name}`}
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative flex max-h-[calc(100dvh-2rem)] w-full max-w-md flex-col items-center overflow-y-auto rounded-2xl border border-border bg-brand-soft p-5 text-center shadow-xl min-[640px]:p-6"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-full border-4 border-background bg-background shadow-md">
          {member.image ? (
            <img
              src={member.thumbnail ?? member.image}
              srcSet={member.thumbnailSrcSet}
              sizes="80px"
              alt={member.imageAlt}
              width={160}
              height={160}
              loading="lazy"
              decoding="async"
              className="h-full w-full object-cover"
            />
          ) : (
            <span aria-hidden="true" className="font-serif text-xl text-brand">
              {initialsOf(member.name)}
            </span>
          )}
        </div>
        <h3 className="mt-2 font-serif text-lg">{member.name}</h3>
        <p className="mt-0.5 text-sm text-muted-foreground">{member.role}</p>
        {paragraphs.map((paragraph, i) => (
          <p
            key={i}
            className="mt-2 self-stretch text-left text-xs leading-relaxed text-muted-foreground min-[640px]:text-sm"
          >
            {paragraph}
          </p>
        ))}
        {member.bioLink ? (
          <Link
            to={member.bioLink}
            className="mt-3 shrink-0 text-sm font-medium text-brand underline underline-offset-2"
          >
            Read the Full Bio
          </Link>
        ) : null}
        <button
          type="button"
          onClick={onClose}
          autoFocus
          className="mt-4 shrink-0 rounded-full border border-brand px-4 py-1.5 text-sm font-medium text-brand transition hover:bg-brand hover:text-brand-foreground"
        >
          Close
        </button>
      </div>
    </div>
  );
}

function TeamMemberCard({ member, index }: { member: TeamMember; index: number }) {
  const [expanded, setExpanded] = useState(false);
  const paragraphs = member.bio ? member.bio.split(/\n\n+/) : [];
  const openBio = () => setExpanded(true);
  const body = (
    <>
      <div className="mx-auto flex aspect-square w-full max-w-[140px] min-[640px]:max-w-[160px] min-[1024px]:max-w-[180px] items-center justify-center overflow-hidden rounded-full border-4 border-background bg-brand-soft shadow-md">
        {member.image ? (
          <img
            src={member.thumbnail ?? member.image}
            srcSet={member.thumbnailSrcSet}
            sizes="(min-width: 1024px) 180px, (min-width: 640px) 160px, 140px"
            alt={member.imageAlt}
            width={180}
            height={180}
            loading={index === 0 ? "eager" : "lazy"}
            decoding="async"
            fetchPriority={index === 0 ? "high" : "auto"}
            className="h-full w-full object-cover"
          />
        ) : (
          <span aria-hidden="true" className="font-serif text-2xl min-[640px]:text-3xl text-brand">
            {initialsOf(member.name)}
          </span>
        )}
      </div>
      <EditableText as="h2" contentKey={`team.member.${member.name}.name`} defaultValue={member.name} className="mt-1 min-[640px]:mt-2 text-sm min-[640px]:text-base font-serif" />
      <EditableText as="p" contentKey={`team.member.${member.name}.role`} defaultValue={member.role} className="mt-0.5 min-[640px]:mt-1 text-xs min-[640px]:text-sm text-muted-foreground" />
      {member.bio ? (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            openBio();
          }}
          aria-haspopup="dialog"
          aria-expanded={expanded}
          className="relative z-10 mt-1 min-[640px]:mt-2 inline-block text-xs min-[640px]:text-sm font-medium text-brand underline underline-offset-2"
        >
          Read Bio
        </button>
      ) : null}
    </>
  );

  const cardClass = "group w-full rounded-2xl border border-border bg-brand-soft p-2 min-[640px]:p-3 text-center transition hover:-translate-y-1 hover:border-brand hover:shadow-lg";

  if (member.bio) {
    return (
      <>
        <article
          onClick={openBio}
          className={`${cardClass} relative cursor-pointer`}
        >
          {body}
          {/* Full bio text stays in the page source so search engines can read it. */}
          {paragraphs.map((paragraph, i) => (
            <span key={i} className="sr-only mt-2 block text-left text-xs min-[640px]:text-sm leading-relaxed text-muted-foreground">
              {paragraph}
            </span>
          ))}
        </article>
        {expanded ? (
          <BioDialog member={member} paragraphs={paragraphs} onClose={() => setExpanded(false)} />
        ) : null}
      </>
    );
  }


  return <article className={cardClass}>{body}</article>;
}

function TeamPage() {
  return (
    <>
      <PageSection className="pt-6 pb-4 text-center">
        <EditableText as="h1" contentKey="team.hero.title" defaultValue="Meet Our Staff" className="text-4xl md:text-5xl font-serif" />
        <EditableText as="p" contentKey="team.hero.body" multiline defaultValue="Let's introduce the caring staff members who assist you on your weight loss journey." className="mt-3 max-w-2xl mx-auto text-muted-foreground" />
      </PageSection>

      <PageSection fullWidth className="bg-brand-soft/30 py-5 md:py-6" innerClassName="grid gap-3 min-[640px]:gap-4 min-[1024px]:gap-5 grid-cols-1 min-[640px]:grid-cols-2 min-[1024px]:grid-cols-4">
          {TEAM_MEMBERS.map((member, index) => {
            const card = <TeamMemberCard member={member} index={index} />;
            return <div key={member.name}>{card}</div>;
          })}
      </PageSection>
    </>
  );
}
